import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ParentAPIService} from "./parent.api.service";
import {CrystalclearWebAssetsBackUrl} from "./config/crystalclear-web-assets-back-url";
import {Observable, of, shareReplay} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {ApsIdFormat} from "../model/clearing/aps-id-format";
import {TraderAggregColumnConfig} from "../model/clearing/trader-aggreg-column-config";
import {PasswordSecurity} from "../model/security/password-security";
import {CrystalclearBackUrl} from "./config/crystalclear-back-url";
import {ApiValidation} from "../model/api/api-validation";

@Injectable({
  providedIn: 'root'
})
export class AppConfigService extends ParentAPIService {

  public webTitle: string;
  public imageCustomerLogoBackground: string;
  public imageCustomerLogoImg: string;
  public imageCustomerLogoSmallImg: string;
  public loginMotion: boolean;

  public clearingStateColorMap: { string: string };
  public clearingExchApsIdFormatMap: { string: ApsIdFormat };
  public clearingReloadInterval: number;
  public clearingAllocationCheckInterval: number;
  public clearingAllocationCheckCallLimit: number;
  public clearingAllocationTaskReloadInterval: number;
  public clearingPalResidualTaskReloadInterval: number;
  public tradeAggregationColumns: TraderAggregColumnConfig[];

  public chartsReloadInterval: number;
  public reportCheckInterval: number;
  public reportCheckCallLimit: number;
  public reportTaskReloadInterval: number;
  public notificationsReloadInterval: number;
  public iceTraderMemoFeature: boolean;
  public healthCheckInterval: number;
  public healthCheckAutoReload: boolean;

  public clearingApsOverrideDefaultEmpty: boolean;

  public clearingAllocationEnabled: boolean;
  public clearingAllocationAccountGiveupMssgAgreeWarning: boolean;
  public clearingTradeSelectionMandatoryFields: string[];
  public clearingAvgPriceDecimal: number;
  public eticketEnabled: boolean;
  public billingEnabled: boolean;
  public supportEnabled: boolean;
  public schedulerEnabled: boolean;
  public healthEnabled: boolean;
  public healthEnabledForAdmin: boolean;

  public notificationEnabled: boolean;
  public cookiesSaveEnabled: boolean;
  public saveLastActivity: boolean;

  public noActivityCheckInterval: number;
  public noActivityEventCheckDuration: number;
  public noActivityTimeout: number;

  public clearingKeepTabSelection: boolean;

  public focusTableMouseOver: boolean;
  public flexGwSuffix: string;
  public flexMaxEditCount: number;
  public flexEnabled: boolean;

  public hogoGasOilPricesCountLimit: number;
  public hogoEnabled: boolean;
  public wsMaxMessageSize: number;
  public logoutOnClose: boolean;
  public tradeAggregationAllModeCountLimit: number;
  public tradeAggregationSecureCountLimit: number;
  public selectionApplyCountLimit: number;
  public allocationHardLimitCount: number;
  public selectionSummaryHardLimitCount: number;
  public allocationWarningLimitCount: number;

  public static NO_OVERRIDE_APS_VALUE = -99999;
  public static OVERRIDE_TO_ZERO = -88888;

  // Cached like `load()` below, but reset on logout (see clearAuthenticatedConfig()) since
  // its contents are only valid for the session that fetched them.
  private authenticatedConfig$: Observable<any> | null = null;

  constructor(http: HttpClient) {
    super(http);
  }

  load(): Observable<any> {
    return this.get(CrystalclearWebAssetsBackUrl.WEB_ASSET_LOAD_CONFIG)
      .pipe(map(data => {
        Object.assign(this, data.item);
        return data.item;
      }),
        catchError(err => {
          console.warn('Config load failed, using defaults', err);
          return of(); // NEVER throw here
        }),
        shareReplay(1));
  }

  /**
   * Loads the feature flags / reload intervals / limits that only matter once past the
   * login page (see AppConfigControllerRest#appConfig, split out of load-config so the
   * unauthenticated endpoint doesn't hand this out to anonymous callers). Safe to call on
   * every guarded navigation: shareReplay(1) means only the first call actually hits the
   * network, subsequent calls replay the cached result — call clearAuthenticatedConfig()
   * on logout so a later login re-fetches instead of replaying a stale/previous user's config.
   */
  loadAuthenticated(): Observable<any> {
    if (!this.authenticatedConfig$) {
      this.authenticatedConfig$ = this.get(CrystalclearWebAssetsBackUrl.WEB_ASSET_APP_CONFIG)
        .pipe(map(data => {
          Object.assign(this, data.item);
          return data.item;
        }),
          catchError(err => {
            console.warn('Authenticated config load failed', err);
            this.authenticatedConfig$ = null; // allow a retry on the next call
            return of();
          }),
          shareReplay(1));
    }
    return this.authenticatedConfig$;
  }

  clearAuthenticatedConfig(): void {
    this.authenticatedConfig$ = null;
  }

  getModulesVersion(): Observable<ApiValidation<any>> {
    return this.get<ApiValidation<any>>(CrystalclearWebAssetsBackUrl.GET_MODULES_VERSION);
  }

}
